import Tooltip from '@mui/material/Tooltip';

import type { ATTRIBUTE_INDICATORS } from './utils.js';

/**
 * Renders attribute indicators in geometric patterns based on count:
 * - 1: single dot
 * - 2: vertical stack
 * - 3: triangle
 * - 4+: repeating pattern (triangle + remaining)
 */
export function AttributeIndicators({
	attributes,
	indicators,
}: {
	attributes: string[];
	indicators: typeof ATTRIBUTE_INDICATORS;
}) {
	const count = attributes.length;

	// Calculate layout based on count
	const getLayout = () => {
		if (count === 1) {
			// Single dot
			return { rows: [[0]] };
		} else if (count === 2) {
			// Vertical stack
			return { rows: [[0], [1]] };
		} else if (count === 3) {
			// Triangle: 1 on top, 2 on bottom
			return { rows: [[0], [1, 2]] };
		} else {
			// For 4+: Create triangles of 3, then repeat
			const rows: number[][] = [];
			let index = 0;

			while (index < count) {
				const remaining = count - index;
				if (remaining === 1) {
					rows.push([index]);
					index++;
				} else if (remaining === 2) {
					rows.push([index], [index + 1]);
					index += 2;
				} else {
					// Triangle pattern
					rows.push([index], [index + 1, index + 2]);
					index += 3;
				}
			}

			return { rows };
		}
	};

	const layout = getLayout();

	const renderDot = (attrIndex: number) => {
		const attr = attributes[attrIndex];
		if (attr === undefined) {
			return null;
		}
		const indicator = indicators[attr];
		if (indicator === undefined) {
			return null;
		}

		return (
			<Tooltip key={attr} title={indicator.label} arrow placement='top' enterDelay={500} enterNextDelay={500}>
				<div
					className='h-[3px] w-[3px] shrink-0 rounded-full transition-all duration-200 ease-in-out hover:scale-150'
					style={{
						backgroundColor: indicator.color,
						boxShadow: `0 0 4px ${indicator.shadowColor}, 0 0 2px ${indicator.shadowColor}`,
					}}
					onMouseEnter={(e) => {
						e.currentTarget.style.boxShadow = `0 0 8px ${indicator.shadowColor}, 0 0 4px ${indicator.shadowColor}`;
					}}
					onMouseLeave={(e) => {
						e.currentTarget.style.boxShadow = `0 0 4px ${indicator.shadowColor}, 0 0 2px ${indicator.shadowColor}`;
					}}
				/>
			</Tooltip>
		);
	};

	return (
		<div className='flex flex-col items-center gap-0.25'>
			{layout.rows.map((row) => (
				<div key={`row-${row.join('-')}`} className='flex items-center gap-2'>
					{row.map((attrIndex) => renderDot(attrIndex))}
				</div>
			))}
		</div>
	);
}
